import type { Coordinate, PatientState } from '@orgsim/shared';
import L from 'leaflet';
import { useMemo } from 'react';
import { Marker } from 'react-leaflet';
import { patientSvg } from '../signs/svg';
import { MarkerConnector, useMarkerPosition } from './MarkerLayout';
import { fromLatLng, toLatLng } from './crs';
import { moveFromDisplay } from './markerGeometry';

type Props = {
  patient: PatientState;
  draggable?: boolean;
  onMove?: (position: Coordinate) => void;
  onClick?: (patient: PatientState) => void;
  selected?: boolean;
};

export function PatientMarker({ patient, draggable = false, onMove, onClick, selected }: Props) {
  const displayed = useMarkerPosition(`patient:${patient.id}`, patient.position, 20);
  const icon = useMemo(
    () =>
      L.divIcon({
        html: patientSvg(patient.number, patient.category),
        iconSize: [34, 34],
        iconAnchor: [17, 17],
        className: `leaflet-div-icon patient-marker${selected ? ' marker-selected' : ''}`,
      }),
    [patient.number, patient.category, selected],
  );
  return (
    <>
      <MarkerConnector actual={patient.position} displayed={displayed} />
      <Marker
        position={toLatLng(displayed)}
        icon={icon}
        draggable={draggable}
        title={
          patient.summary
            ? `Patient ${patient.number}: ${patient.summary}`
            : `Patient ${patient.number}`
        }
        alt={`Patient ${patient.number}`}
        zIndexOffset={selected ? 1100 : 100}
        opacity={patient.destination ? 0.4 : 1}
        eventHandlers={{
          dragend: (e) =>
            onMove?.(
              moveFromDisplay(
                patient.position,
                displayed,
                fromLatLng((e.target as L.Marker).getLatLng()),
              ),
            ),
          click: () => onClick?.(patient),
        }}
      />
    </>
  );
}
